"use client";
import { Plus, Minus } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Container } from "@/components/container";

export function Manifesto({ compact = false }: { compact?: boolean } = {}) {
  const [isFull, setIsFull] = useState(false);

  // Funnel mode: the snapshot tool is the point, so the long manifesto
  // is collapsed to a single sentence rather than pushing the funnel
  // down the page. The full version still ships on the default homepage.
  if (compact) {
    return (
      <section>
        <Container className="border-t-0 py-16 max-lg:px-6 lg:py-24">
          <p className="text-muted-foreground mx-auto max-w-2xl text-balance text-center text-xl leading-relaxed md:text-2xl">
            People increasingly get answers straight from AI instead of a list
            of links&mdash;{" "}
            <strong className="text-foreground font-medium">
              Ripenn shows you whether those answers recommend your brand
            </strong>
            , and where they don&apos;t.
          </p>
        </Container>
      </section>
    );
  }

  return (
    <section>
      <Container className="border-t-0 py-16 max-lg:px-6 lg:py-24">
        <div
          data-state={isFull ? "full" : "collapsed"}
          className="relative mx-auto max-w-2xl"
        >
          <motion.div
            className={cn(
              "relative overflow-hidden",
              !isFull && "mask-b-from-45%",
            )}
            initial={{ height: "22rem" }}
            animate={{ height: isFull ? "auto" : "22rem" }}
            exit={{ height: "22rem" }}
          >
            <div className="text-muted-foreground space-y-4 text-xl *:leading-relaxed md:text-2xl">
              <p>
                The way people discover information has{" "}
                <strong className="text-foreground font-medium">
                  fundamentally changed
                </strong>
                . When someone asks ChatGPT a question, searches in Perplexity,
                or queries Claude, they&apos;re not clicking through ten blue
                links—they&apos;re getting{" "}
                <strong className="text-foreground font-medium">
                  direct answers with citations
                </strong>
                .
              </p>

              <p>
                This represents the{" "}
                <strong className="text-foreground font-medium">
                  most significant shift in search since Google
                </strong>
                . Yet most content strategies remain anchored to ranking signals
                that were never designed for AI-driven discovery.
              </p>

              <p>
                Content that gets cited by AI systems isn&apos;t optimized
                through shortcuts. It succeeds because it&apos;s{" "}
                <strong className="text-foreground font-medium">
                  genuinely better
                </strong>
                &ndash;clearer structure, credible sources, and direct answers
                to user intent.
              </p>

              {/* Content below "Read More" fold */}

              <p>
                As AI systems increasingly model how people read, reason, and
                synthesize information, the signals they rely on&mdash;
                <strong className="text-foreground font-medium">
                  clarity, structure, factual grounding, and coherence
                </strong>
                &ndash;directly mirror what human audiences value.
              </p>

              <p>
                Traditional SEO foundations still matter: technical
                fundamentals, site authority, and crawlability. But{" "}
                <strong className="text-foreground font-medium">
                  on-page tactics designed to game ranking algorithms
                </strong>
                —keyword stuffing, header manipulation, thin content scaled for
                volume—don&apos;t translate to AI-driven discovery.
              </p>

              <p>
                AI systems evaluate content holistically and cite sources based
                on{" "}
                <strong className="text-foreground font-medium">
                  comprehension and trust
                </strong>
                , not pattern matching. This is where{" "}
                <strong className="text-foreground font-medium">
                  Generative Engine Optimization
                </strong>{" "}
                becomes essential: a systematic approach to improving content
                quality at scale. Rather than chasing ranking signals, GEO
                focuses on the clarity, structure, and authority that both AI
                systems and human readers rely on.
              </p>

              <p>
                When you optimize for genuine understanding, you don&apos;t just
                rank better.{" "}
                <strong className="text-foreground font-medium">
                  You get cited, referenced, and trusted
                </strong>
                .
              </p>
            </div>
          </motion.div>
          <div className="group relative mt-6 w-fit">
            <CardDecorator className="group-hover:scale-115 border-primary size-2" />
            <Button
              onClick={() => setIsFull(!isFull)}
              className="flex rounded pr-2.5"
              variant="ghost"
              size="sm"
            >
              <span>Read {isFull ? "Less" : "More"}</span>
              {isFull ? (
                <Minus
                  strokeWidth={2.5}
                  className="!size-3.5 opacity-50 duration-300"
                />
              ) : (
                <Plus
                  strokeWidth={2.5}
                  className="!size-3.5 opacity-50 duration-300 group-hover:rotate-90"
                />
              )}
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
}

const CardDecorator = ({ className }: { className?: string }) => (
  <>
    <span
      className={cn(
        "border-primary rounded-tl-xs absolute -left-px -top-px block size-2 border-l border-t",
        className,
      )}
    ></span>
    <span
      className={cn(
        "border-primary rounded-tr-xs absolute -right-px -top-px block size-2 border-r border-t",
        className,
      )}
    ></span>
    <span
      className={cn(
        "border-primary rounded-bl-xs absolute -bottom-px -left-px block size-2 border-b border-l",
        className,
      )}
    ></span>
    <span
      className={cn(
        "border-primary rounded-br-xs absolute -bottom-px -right-px block size-2 border-b border-r",
        className,
      )}
    ></span>
  </>
);
